import { useState, useEffect } from "react";
import { motion, AnimatePresence, animate } from "framer-motion";
import { Star, MessageSquareQuote, Sparkles } from "lucide-react";

const ICONS = [Star, MessageSquareQuote, Sparkles];

const parseValue = (raw) => {
  const suffix = raw.includes("+") ? "+" : "";
  const sep = raw.includes(",") ? "," : ".";
  const numStr = raw.replace("+", "").replace(",", ".");
  const target = parseFloat(numStr) || 0;
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return { target, decimals, suffix, sep };
};

const CountUp = ({ value }) => {
  const { target, decimals, suffix, sep } = parseValue(value);
  const [disp, setDisp] = useState("0");
  useEffect(() => {
    const controls = animate(0, target, {
      duration: 1.4,
      delay: 0.25,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        let s = v.toFixed(decimals);
        if (sep === ",") s = s.replace(".", ",");
        setDisp(s);
      },
    });
    return () => controls.stop();
  }, [target, decimals, sep]);
  return (
    <>
      {disp}
      {suffix}
    </>
  );
};

export const StatsRibbon = ({ stats }) => {
  const [show, setShow] = useState(false);
  const [onDark, setOnDark] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShow(window.scrollY > 40);
      const probeY = window.innerHeight - 60;
      const darkEls = [
        document.getElementById("booking"),
        document.querySelector('[data-testid="site-footer"]'),
      ];
      const over = darkEls.some((el) => {
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top <= probeY && r.bottom >= probeY;
      });
      setOnDark(over);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToReviews = () => {
    const target = document.getElementById("voices");
    if (window.__lenis) window.__lenis.scrollTo(target, { offset: -90 });
    else if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 md:bottom-6 left-1/2 z-40 -translate-x-1/2"
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 -z-10 rounded-full bg-[#FAFAF7] blur-2xl scale-[1.35] transition-opacity duration-500 ${
              onDark ? "opacity-30" : "opacity-0"
            }`}
          />
          <button
            type="button"
            onClick={goToReviews}
            data-testid="stats-ribbon"
            aria-label="Voir les avis clients"
            className={`group relative flex items-center gap-4 sm:gap-7 md:gap-10 rounded-full border bg-[#172033]/85 backdrop-blur-xl px-5 sm:px-8 md:px-10 py-3 md:py-4 shadow-[0_14px_50px_rgba(10,15,25,0.35)] cursor-pointer transition-colors duration-500 ${
              onDark ? "border-[#B0863A]/70" : "border-[#B0863A]/45 hover:border-[#B0863A]"
            }`}
          >
            {stats.map((s, i) => {
              const Icon = ICONS[i];
              return (
                <div key={i} className="flex items-center gap-4 sm:gap-7 md:gap-10">
                  <div className="flex items-center gap-2.5 md:gap-3">
                    {Icon && (
                      <Icon
                        size={18}
                        strokeWidth={1.6}
                        className={`shrink-0 text-[#B0863A] origin-center ${i === 0 ? "fill-[#B0863A] group-hover:[animation:starPulse_1.1s_ease-in-out_infinite]" : ""}`}
                      />
                    )}
                    <div className="flex flex-col items-start leading-none">
                      <span className="font-serif-display text-[#F6F5F1] text-xl sm:text-2xl md:text-3xl leading-none tabular-nums">
                        <CountUp value={s.value} />
                      </span>
                      <span className="mt-1.5 text-[#D9B87A] text-[0.5rem] sm:text-[0.55rem] md:text-[0.62rem] uppercase tracking-[0.16em] whitespace-nowrap">
                        {s.label}
                      </span>
                    </div>
                  </div>
                  {i < stats.length - 1 && (
                    <span className="h-8 md:h-9 w-px bg-[#F6F5F1]/15" aria-hidden="true" />
                  )}
                </div>
              );
            })}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
